import { Fragment, useCallback, useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { Button } from '../../ds';
import { ErrorState, LoadingState } from '../../components/ScreenState';
import { useAsync } from '../../hooks/useAsync';
import { useAuth } from '../../auth/AuthContext';
import { mockTestApi } from '../../api/mockTest';
import { API_BASE_URL } from '../../api/client';
import type { LeaderboardSort, MockTestAttemptDetail, MockTestQuestion } from '../../api/mockTest.types';
import { formatAttemptMeta } from '../../shell/exerciseHistoryFormat';
import { WEB_ICO } from '../../shell/icons';
import {
  BLANK,
  PART_META,
  answeredIn,
  formatClock,
  newSession,
  optionsInGrid,
  partQuestions,
  partStatus,
  splitUnderline,
  verdict,
  type ExamSession,
} from './mockTestLogic';

const card: CSSProperties = { background: 'var(--surface-page)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-card)' };
const jp: CSSProperties = { fontFamily: 'var(--font-jp)' };
const fullBtn: CSSProperties = { width: '100%', justifyContent: 'center', whiteSpace: 'nowrap', paddingLeft: 12, paddingRight: 12 };
const NAME_MAX = 24;

type Screen = 'home' | 'exam' | 'part' | 'result' | 'review';
type HomeTab = 'do' | 'history' | 'rank';

/**
 * Phát phần nghe của 1 câu. Đề có mp3 (lưu trong DB, xem MockTestAudio) thì phát đúng đoạn
 * [audioStartMs, audioEndMs) của câu trong file chung; chưa có thì đọc `script` bằng Web Speech,
 * dòng sau phát khi dòng trước xong. Rời câu thì dừng.
 */
function useListeningPlayer(audioUrl: string | null) {
  const [playing, setPlaying] = useState(false);
  const token = useRef(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const stopAt = useRef(0);

  // 1 thẻ <audio> cho cả đề — preload metadata để lần bấm đầu tua tới đoạn nhanh.
  useEffect(() => {
    if (!audioUrl) return;
    const el = new Audio(audioUrl);
    el.preload = 'auto';
    const onTime = () => {
      if (el.currentTime * 1000 >= stopAt.current) {
        el.pause();
        setPlaying(false);
      }
    };
    const onEnd = () => setPlaying(false);
    el.addEventListener('timeupdate', onTime);
    el.addEventListener('ended', onEnd);
    el.addEventListener('error', onEnd);
    audioRef.current = el;
    return () => {
      el.pause();
      el.removeEventListener('timeupdate', onTime);
      el.removeEventListener('ended', onEnd);
      el.removeEventListener('error', onEnd);
      audioRef.current = null;
    };
  }, [audioUrl]);

  const stop = useCallback(() => {
    token.current++;
    audioRef.current?.pause();
    if ('speechSynthesis' in window) window.speechSynthesis.cancel();
    setPlaying(false);
  }, []);

  const play = useCallback(
    (q: Pick<MockTestQuestion, 'script' | 'audioStartMs' | 'audioEndMs'>) => {
      stop();
      const el = audioRef.current;
      if (el && q.audioStartMs != null && q.audioEndMs != null) {
        stopAt.current = q.audioEndMs;
        el.currentTime = q.audioStartMs / 1000;
        setPlaying(true);
        el.play().catch(() => setPlaying(false));
        return;
      }
      if (!('speechSynthesis' in window)) return;
      const mine = token.current;
      setPlaying(true);
      const next = (i: number) => {
        if (token.current !== mine) return;
        if (i >= q.script.length) return setPlaying(false);
        const u = new SpeechSynthesisUtterance(q.script[i].replace(/…/g, '、'));
        u.lang = 'ja-JP';
        u.rate = 0.85;
        u.onend = () => next(i + 1);
        u.onerror = () => token.current === mine && setPlaying(false);
        window.speechSynthesis.speak(u);
      };
      next(0);
    },
    [stop],
  );

  useEffect(() => () => stop(), [stop]);
  return { playing, play, stop };
}

function Header({ title, sub, onBack, right }: { title: string; sub?: string; onBack: () => void; right?: ReactNode }) {
  return (
    <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--border-default)', flexShrink: 0 }}>
      <button
        onClick={onBack}
        aria-label="Quay lại"
        style={{ width: 40, height: 40, borderRadius: 8, border: '1px solid var(--border-default)', background: 'var(--surface-page)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
      >
        {WEB_ICO.back()}
      </button>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</div>
        {sub && <div style={{ fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sub}</div>}
      </div>
      {right}
    </div>
  );
}

function Pill<K extends string>({ items, value, onChange }: { items: [K, string][]; value: K; onChange: (k: K) => void }) {
  return (
    <div role="tablist" style={{ display: 'flex', gap: 6, background: 'var(--surface-tint)', borderRadius: 999, padding: 4 }}>
      {items.map(([k, label]) => (
        <button
          key={k}
          role="tab"
          aria-selected={value === k}
          onClick={() => onChange(k)}
          style={{
            flex: 1,
            height: 38,
            borderRadius: 999,
            border: 'none',
            cursor: 'pointer',
            fontFamily: 'inherit',
            fontSize: 13.5,
            fontWeight: 700,
            background: value === k ? 'var(--cta-default)' : 'transparent',
            color: value === k ? '#fff' : 'var(--text-secondary)',
          }}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function Passage({ text }: { text: string }) {
  return <div style={{ ...card, background: 'var(--surface-tint)', padding: 14, fontSize: 15, lineHeight: 1.8, color: 'var(--ink)', whiteSpace: 'pre-line', ...jp }}>{text}</div>;
}

function Prompt({ q, picked }: { q: Pick<MockTestQuestion, 'prompt' | 'options'>; picked?: number }) {
  if (q.prompt.includes(BLANK)) {
    const parts = q.prompt.split(BLANK);
    return (
      <>
        {parts.map((t, i) => (
          <Fragment key={i}>
            {t}
            {i < parts.length - 1 && (
              <span
                style={{
                  display: 'inline-block',
                  minWidth: 56,
                  margin: '0 4px',
                  padding: '0 8px',
                  textAlign: 'center',
                  borderBottom: '3px solid var(--cta-default)',
                  background: picked && parts.length === 2 ? 'var(--surface-tint)' : 'transparent',
                  borderRadius: 6,
                  color: 'var(--cta-default)',
                }}
              >
                {picked && parts.length === 2 ? q.options[picked - 1] : '\u00a0'}
              </span>
            )}
          </Fragment>
        ))}
      </>
    );
  }
  return (
    <>
      {splitUnderline(q.prompt).map((seg, i) =>
        i % 2 === 1 ? (
          <u key={i} style={{ textUnderlineOffset: 5, textDecorationThickness: 2 }}>
            {seg}
          </u>
        ) : (
          <Fragment key={i}>{seg}</Fragment>
        ),
      )}
    </>
  );
}

interface MockTestScreenProps {
  mockTestId: string;
  title: string;
  /** Rời màn (về lộ trình) — shell fetch lại danh sách Bài test để thẻ hiện điểm mới. */
  onExit: () => void;
  /** Báo shell còn bài đang làm chưa nộp — để hỏi lại khi bấm thoát chế độ học tập. */
  onDirtyChange?: (dirty: boolean) => void;
}

/**
 * "Bài test" thi thử JLPT N5 sau mỗi 5 Bài — bản web của apps/mobile/src/screens/hoc/mocktest/MockTestScreen.tsx,
 * cùng luồng view: trang chủ (Làm bài/Lịch sử/Xếp hạng) → tổng quan 3 phần → làm từng phần → nộp
 * (nhập tên) → kết quả → xem lại đáp án. Server chỉ chấm khi nộp CẢ bài.
 */
export function MockTestScreen({ mockTestId, title, onExit, onDirtyChange }: MockTestScreenProps) {
  const { user } = useAuth();

  const test = useAsync(() => mockTestApi.get(mockTestId), [mockTestId]);
  const player = useListeningPlayer(test.data?.audio ? `${API_BASE_URL}${test.data.audio.path}` : null);
  const [reloadKey, setReloadKey] = useState(0);
  const history = useAsync(() => mockTestApi.listAttempts(mockTestId), [mockTestId, reloadKey]);
  const [rankSort, setRankSort] = useState<LeaderboardSort>('score');
  const board = useAsync(() => mockTestApi.leaderboard(mockTestId, rankSort), [mockTestId, rankSort, reloadKey]);

  const [screen, setScreen] = useState<Screen>('home');
  const [tab, setTab] = useState<HomeTab>('do');
  const [session, setSession] = useState<ExamSession | null>(null);
  const [part, setPart] = useState(0);
  const [qIndex, setQIndex] = useState(0);
  const [confirm, setConfirm] = useState<'finishPart' | 'submit' | 'restart' | null>(null);
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState<MockTestAttemptDetail | null>(null);
  const [reviewFilter, setReviewFilter] = useState<'all' | 'wrong'>('all');
  const [notice, setNotice] = useState<string | null>(null);

  const partSeconds = test.data?.partSeconds ?? 600;
  const questions = test.data?.questions ?? [];
  const parts = [1, 2, 3].map((p) => partQuestions(questions, p));

  const dirty = session != null && Object.keys(session.answers).length > 0;
  useEffect(() => onDirtyChange?.(dirty), [dirty, onDirtyChange]);

  // Đồng hồ theo mốc thời gian lúc vào phần — xem bản mobile.
  const [clock, setClock] = useState<{ part: number; base: number; since: number } | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const elapsedOf = (c: { base: number; since: number }, at: number) => c.base + Math.floor((at - c.since) / 1000);

  const leavePart = useCallback(
    (lock: boolean) => {
      if (clock) {
        const used = Math.min(partSeconds, elapsedOf(clock, Date.now()));
        setSession((s) =>
          s
            ? {
                ...s,
                used: s.used.map((x, i) => (i === clock.part ? used : x)) as ExamSession['used'],
                locked: s.locked.map((x, i) => (i === clock.part && lock ? true : x)) as ExamSession['locked'],
              }
            : s,
        );
      }
      setClock(null);
      setConfirm(null);
      setScreen('exam');
    },
    [clock, partSeconds],
  );

  useEffect(() => {
    if (!clock) return;
    const t = setInterval(() => {
      if (elapsedOf(clock, Date.now()) < partSeconds) return setNow(Date.now());
      leavePart(true);
      setNotice(`Hết giờ phần ${clock.part + 1} — phần này đã được khoá.`);
    }, 500);
    return () => clearInterval(t);
  }, [clock, partSeconds, leavePart]);

  const { stop } = player;
  useEffect(() => stop(), [screen, part, qIndex, stop]);

  const openPart = useCallback(
    (p: number) => {
      if (!session) return;
      const since = Date.now();
      setClock({ part: p, base: session.used[p], since });
      setNow(since);
      setPart(p);
      setQIndex(0);
      setNotice(null);
      setScreen('part');
    },
    [session],
  );

  if (test.loading) return <LoadingState />;
  if (test.error || !test.data) return <ErrorState message="Không tải được bài test." onRetry={onExit} />;

  const data = test.data;
  const total = questions.length;
  const minutes = Math.round(partSeconds / 60);

  const startNew = () => {
    setSession(newSession());
    setConfirm(null);
    setNotice(null);
    setScreen('exam');
  };
  const submit = async () => {
    if (!session) return;
    const trimmed = name.trim();
    if (!trimmed) return setNameError(true);
    setSubmitting(true);
    setError(null);
    try {
      const result = await mockTestApi.submit(mockTestId, {
        displayName: trimmed,
        answers: Object.entries(session.answers).map(([order, picked]) => ({ order: Number(order), picked })),
        partSeconds: session.used,
      });
      setSession(null);
      setConfirm(null);
      setAttempt(result);
      setReloadKey((k) => k + 1);
      setScreen('result');
    } catch {
      setError('Không nộp được bài — kiểm tra mạng rồi thử lại.');
    } finally {
      setSubmitting(false);
    }
  };
  const openAttempt = async (attemptId: string) => {
    try {
      setAttempt(await mockTestApi.getAttempt(attemptId));
      setReviewFilter('all');
      setScreen('review');
    } catch {
      setNotice('Không tải được lượt làm bài này.');
    }
  };

  const shell = (header: ReactNode, body: ReactNode, footer?: ReactNode) => (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, fontFamily: 'var(--font-vi)' }}>
      {header}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 20 }}>{body}</div>
      {footer && <div style={{ padding: '14px 20px calc(14px + env(safe-area-inset-bottom, 0px))', borderTop: '1px solid var(--border-default)', flexShrink: 0 }}>{footer}</div>}
    </div>
  );

  /* ---------- làm từng phần ---------- */
  if (screen === 'part' && session) {
    const list = parts[part];
    const q = list[qIndex];
    const picked = session.answers[q.order];
    const left = partSeconds - (clock ? elapsedOf(clock, now) : session.used[part]);
    const last = qIndex === list.length - 1;
    const blank = list.filter((x) => session.answers[x.order] == null).length;
    const grid = optionsInGrid(q);
    return shell(
      <>
        <Header
          title={`Phần ${part + 1} · ${PART_META[part].vi}`}
          sub={`Câu ${q.order} · ${qIndex + 1}/${list.length}`}
          onBack={() => leavePart(false)}
          right={
            <div
              style={{
                fontSize: 14,
                fontWeight: 700,
                fontVariantNumeric: 'tabular-nums',
                color: left < 60 ? 'var(--error)' : 'var(--ink)',
                background: left < 60 ? 'var(--error-bg)' : 'var(--surface-tint)',
                borderRadius: 999,
                padding: '6px 12px',
              }}
            >
              {formatClock(left)}
            </div>
          }
        />
        <div style={{ height: 4, background: 'var(--border-default)', flexShrink: 0 }}>
          <div style={{ height: '100%', width: `${((qIndex + 1) / list.length) * 100}%`, background: 'var(--cta-default)', transition: 'width .25s' }} />
        </div>
        <div style={{ display: 'flex', gap: 6, padding: '10px 16px', overflowX: 'auto', flexShrink: 0 }}>
          {list.map((x, i) => {
            const done = session.answers[x.order] != null;
            return (
              <button
                key={x.order}
                onClick={() => setQIndex(i)}
                aria-label={`Câu ${x.order}`}
                style={{
                  width: 34,
                  height: 34,
                  flexShrink: 0,
                  borderRadius: '50%',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  fontSize: 12,
                  fontWeight: 700,
                  border: i === qIndex ? '2px solid var(--cta-default)' : '1px solid var(--border-default)',
                  background: done ? 'var(--cta-default)' : 'var(--surface-page)',
                  color: done ? '#fff' : 'var(--text-secondary)',
                }}
              >
                {x.order}
              </button>
            );
          })}
        </div>
      </>,
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--cta-default)', ...jp }}>{q.mondai}</div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4, lineHeight: 1.5, ...jp }}>{q.instruction}</div>
        </div>
        {q.passage && <Passage text={q.passage} />}
        {q.script.length > 0 && (
          <button
            onClick={() => (player.playing ? player.stop() : player.play(q))}
            style={{
              height: 48,
              borderRadius: 'var(--radius-control)',
              border: `1px solid ${player.playing ? 'var(--cta-default)' : 'var(--border-default)'}`,
              background: player.playing ? 'var(--surface-tint)' : 'var(--surface-page)',
              color: 'var(--cta-default)',
              fontFamily: 'inherit',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
            }}
          >
            {WEB_ICO.play('var(--cta-default)', 16)}
            {player.playing ? 'Đang phát… bấm để dừng' : 'Phát đoạn nghe'}
          </button>
        )}
        <div style={{ ...card, padding: 16, fontSize: 18, fontWeight: 600, lineHeight: 1.9, color: 'var(--ink)', whiteSpace: 'pre-line', ...jp }}>
          <Prompt q={q} picked={picked} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: grid ? '1fr 1fr' : '1fr', gap: 10 }}>
          {q.options.map((o, i) => {
            const on = picked === i + 1;
            return (
              <button
                key={i}
                role="radio"
                aria-checked={on}
                onClick={() => setSession((s) => (s ? { ...s, answers: { ...s.answers, [q.order]: i + 1 } } : s))}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  minHeight: 56,
                  padding: '10px 12px',
                  textAlign: 'left',
                  borderRadius: 'var(--radius-control)',
                  border: `2px solid ${on ? 'var(--cta-default)' : 'var(--border-default)'}`,
                  background: on ? 'var(--surface-tint)' : 'var(--surface-page)',
                  fontSize: 16,
                  color: 'var(--ink)',
                  cursor: 'pointer',
                  ...jp,
                }}
              >
                <span
                  style={{
                    width: 26,
                    height: 26,
                    flexShrink: 0,
                    borderRadius: '50%',
                    background: on ? 'var(--cta-default)' : 'var(--surface-tint)',
                    color: on ? '#fff' : 'var(--text-secondary)',
                    fontSize: 13,
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontFamily: 'var(--font-vi)',
                  }}
                >
                  {i + 1}
                </span>
                {!q.optionsHidden && o}
              </button>
            );
          })}
        </div>
      </div>,
      confirm === 'finishPart' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ fontSize: 13.5, color: 'var(--ink)', background: 'var(--surface-tint)', borderRadius: 10, padding: '10px 12px', lineHeight: 1.5 }}>
            {`${blank > 0 ? `Còn ${blank} câu chưa trả lời. ` : ''}Hoàn thành rồi thì không quay lại sửa phần này được.`}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="secondary" size="lg" style={{ ...fullBtn, flex: 1 }} onClick={() => setConfirm(null)}>
              Làm tiếp
            </Button>
            <Button size="lg" style={{ ...fullBtn, flex: 1 }} onClick={() => leavePart(true)}>
              Hoàn thành
            </Button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 8 }}>
          {qIndex > 0 && (
            <Button variant="secondary" size="lg" style={{ ...fullBtn, flex: 1 }} onClick={() => setQIndex(qIndex - 1)}>
              Câu trước
            </Button>
          )}
          <Button size="lg" style={{ ...fullBtn, flex: 2 }} onClick={() => (last ? setConfirm('finishPart') : setQIndex(qIndex + 1))}>
            {last ? 'Hoàn thành phần này' : picked != null ? 'Câu tiếp theo' : 'Bỏ qua'}
          </Button>
        </div>
      ),
    );
  }

  /* ---------- tổng quan 3 phần ---------- */
  if (screen === 'exam' && session) {
    const done = answeredIn(session, questions);
    const blank = total - done;
    return shell(
      <Header title={data.title} sub={`${done}/${total} câu đã trả lời`} onBack={() => setScreen('home')} />,
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {notice && <div style={{ fontSize: 13.5, color: 'var(--warning-text)', background: 'var(--warning-bg)', borderRadius: 10, padding: '10px 12px' }}>{notice}</div>}
        {parts.map((list, i) => {
          const status = partStatus(session, list, i);
          const locked = session.locked[i];
          return (
            <button
              key={i}
              disabled={locked}
              onClick={() => openPart(i)}
              style={{ ...card, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 14, width: '100%', textAlign: 'left', fontFamily: 'inherit', cursor: locked ? 'default' : 'pointer', opacity: locked ? 0.85 : 1 }}
            >
              <span
                style={{
                  width: 44,
                  height: 44,
                  flexShrink: 0,
                  borderRadius: '50%',
                  background: status.tone === 'done' ? 'var(--success)' : 'var(--surface-tint)',
                  color: status.tone === 'done' ? '#fff' : 'var(--cta-default)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: status.tone === 'done' ? 20 : 13,
                  fontWeight: 700,
                  ...jp,
                }}
              >
                {status.tone === 'done' ? '✓' : PART_META[i].short}
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 15, fontWeight: 600, color: 'var(--ink)' }}>{`Phần ${i + 1} · ${PART_META[i].vi}`}</span>
                <span style={{ display: 'block', fontSize: 12.5, color: 'var(--text-secondary)' }}>
                  {`${list.length} câu · ${locked ? 'Đã khoá' : `${formatClock(partSeconds - session.used[i])} còn lại`}`}
                </span>
              </span>
              <span
                style={{
                  fontSize: 12.5,
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                  color: status.tone === 'done' ? 'var(--success)' : status.tone === 'doing' ? 'var(--cta-default)' : 'var(--text-secondary)',
                }}
              >
                {status.label}
              </span>
            </button>
          );
        })}
        <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5, margin: '4px 2px' }}>
          {`Mỗi phần có ${minutes} phút, đồng hồ chỉ chạy khi bạn đang ở trong phần đó. Làm xong phần nào thì quay về đây chọn phần tiếp theo. Điểm chỉ được tính khi bạn bấm Nộp bài.`}
        </div>
      </div>,
      confirm === 'submit' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {blank > 0 && (
            <div style={{ fontSize: 13.5, color: 'var(--warning-text)', background: 'var(--warning-bg)', borderRadius: 10, padding: '10px 12px' }}>
              {`Còn ${blank} câu chưa trả lời — các câu này tính 0 điểm.`}
            </div>
          )}
          <label style={{ display: 'block' }}>
            <span style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--ink)', marginBottom: 6 }}>
              Tên hiển thị trên bảng xếp hạng <span style={{ color: 'var(--error)' }}>*</span>
            </span>
            <input
              value={name}
              maxLength={NAME_MAX}
              autoFocus
              placeholder="Ví dụ: Lan Anh"
              onChange={(e) => {
                setName(e.target.value);
                setNameError(false);
              }}
              onKeyDown={(e) => e.key === 'Enter' && submit()}
              style={{
                width: '100%',
                height: 48,
                padding: '0 14px',
                borderRadius: 'var(--radius-control)',
                border: `1.5px solid ${nameError ? 'var(--error)' : 'var(--border-default)'}`,
                fontSize: 16,
                fontFamily: 'inherit',
                color: 'var(--ink)',
                background: 'var(--surface-page)',
                outline: 'none',
              }}
            />
            {nameError && <span style={{ display: 'block', fontSize: 12.5, color: 'var(--error)', marginTop: 6 }}>Nhập tên để nộp bài.</span>}
          </label>
          {error && <div style={{ fontSize: 13, color: 'var(--error)' }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="secondary" size="lg" style={{ ...fullBtn, flex: 1 }} disabled={submitting} onClick={() => setConfirm(null)}>
              Làm tiếp
            </Button>
            <Button size="lg" style={{ ...fullBtn, flex: 1 }} disabled={submitting} onClick={submit}>
              {submitting ? 'Đang chấm…' : 'Nộp bài'}
            </Button>
          </div>
        </div>
      ) : (
        <Button
          size="lg"
          style={fullBtn}
          disabled={done === 0}
          onClick={() => {
            setName((n) => n || history.data?.items[0]?.displayName || user?.displayName || '');
            setNameError(false);
            setError(null);
            setConfirm('submit');
          }}
        >
          Nộp bài
        </Button>
      ),
    );
  }

  /* ---------- kết quả ---------- */
  if (screen === 'result' && attempt) {
    const boxes: [string, string, boolean][] = [
      ['Từ vựng · Ngữ pháp · Đọc', `${attempt.knowledgeScore}/120`, attempt.knowledgeScore < 38],
      ['Nghe hiểu', `${attempt.listeningScore}/60`, attempt.listeningScore < 19],
      ['Xếp hạng', attempt.rank ? `#${attempt.rank}` : '—', false],
    ];
    return shell(
      <Header title="Kết quả" sub={`${attempt.displayName} · ${data.title}`} onBack={() => setScreen('home')} />,
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ ...card, padding: 20, textAlign: 'center', background: 'var(--surface-tint)' }}>
          <div style={{ fontSize: 44, fontWeight: 700, color: 'var(--ink)', lineHeight: 1 }}>
            {attempt.totalScore}
            <span style={{ fontSize: 18, color: 'var(--text-secondary)' }}>/180</span>
          </div>
          <div style={{ fontSize: 13.5, color: 'var(--text-secondary)', marginTop: 8 }}>{`Đúng ${attempt.correct}/${attempt.total} câu · ${formatClock(attempt.durationSeconds)}`}</div>
          <div style={{ fontSize: 13.5, color: 'var(--ink)', marginTop: 6, fontWeight: 600, lineHeight: 1.5 }}>{verdict(attempt.totalScore, attempt.passed)}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, marginTop: 16 }}>
            {boxes.map(([label, value, bad]) => (
              <div key={label} style={{ background: 'var(--surface-page)', borderRadius: 12, padding: '10px 4px' }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: bad ? 'var(--error)' : 'var(--ink)' }}>{value}</div>
                <div style={{ fontSize: 11, color: 'var(--text-secondary)', lineHeight: 1.3 }}>{label}</div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ ...card, padding: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {PART_META.map((m, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, color: 'var(--ink)' }}>
              <span>{`Phần ${i + 1} · ${m.vi}`}</span>
              <b>{`${attempt.partCorrect[i]}/${parts[i].length} · ${formatClock(attempt.partSeconds[i])}`}</b>
            </div>
          ))}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>Quy đổi tuyến tính: câu 1–20 → /120, câu 21–29 → /60. JLPT thật dùng điểm chuẩn hoá.</div>
      </div>,
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Button
          size="lg"
          style={fullBtn}
          onClick={() => {
            setReviewFilter(attempt.correct < attempt.total ? 'wrong' : 'all');
            setScreen('review');
          }}
        >
          Xem lại đáp án
        </Button>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            variant="secondary"
            style={{ ...fullBtn, flex: 1 }}
            onClick={() => {
              setTab('rank');
              setScreen('home');
            }}
          >
            Bảng xếp hạng
          </Button>
          <Button variant="secondary" style={{ ...fullBtn, flex: 1 }} onClick={onExit}>
            Về lộ trình
          </Button>
        </div>
      </div>,
    );
  }

  /* ---------- xem lại đáp án ---------- */
  if (screen === 'review' && attempt) {
    const wrong = attempt.questions.filter((q) => q.picked !== q.answer);
    const shown = reviewFilter === 'wrong' ? wrong : attempt.questions;
    return shell(
      <>
        <Header title="Xem lại đáp án" sub={`${formatAttemptMeta(attempt)} · ${attempt.totalScore}/180`} onBack={() => setScreen('home')} />
        <div style={{ padding: '12px 20px 0' }}>
          <Pill
            items={[
              ['all', `Tất cả (${attempt.questions.length})`],
              ['wrong', `Câu sai (${wrong.length})`],
            ]}
            value={reviewFilter}
            onChange={setReviewFilter}
          />
        </div>
      </>,
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {shown.length === 0 && <div style={{ ...card, padding: 24, textAlign: 'center', fontSize: 14, color: 'var(--text-secondary)' }}>Bạn đúng toàn bộ — không có câu sai.</div>}
        {shown.map((q) => {
          const ok = q.picked === q.answer;
          return (
            <div key={q.order} style={{ ...card, padding: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 700, color: 'var(--ink)', marginBottom: 10 }}>
                <span
                  style={{ width: 22, height: 22, borderRadius: '50%', background: ok ? 'var(--success)' : 'var(--error)', color: '#fff', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  {ok ? '✓' : '✕'}
                </span>
                {`Câu ${q.order} · ${PART_META[q.part - 1].vi}`}
              </div>
              {q.passage && (
                <div style={{ marginBottom: 10 }}>
                  <Passage text={q.passage} />
                </div>
              )}
              {q.script.length > 0 && (
                <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.7, marginBottom: 8, whiteSpace: 'pre-line', ...jp }}>{`Lời thoại:\n${q.script.join('\n')}`}</div>
              )}
              <div style={{ fontSize: 16, fontWeight: 600, lineHeight: 1.8, color: 'var(--ink)', whiteSpace: 'pre-line', marginBottom: 10, ...jp }}>
                <Prompt q={q} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {q.options.map((o, k) => {
                  const isAnswer = k + 1 === q.answer;
                  const isPicked = k + 1 === q.picked;
                  const tone = isAnswer ? 'var(--success)' : isPicked ? 'var(--error)' : 'var(--border-default)';
                  return (
                    <div
                      key={k}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '8px 10px',
                        borderRadius: 8,
                        border: `1.5px solid ${tone}`,
                        background: isAnswer ? 'var(--success-bg)' : isPicked ? 'var(--error-bg)' : 'transparent',
                        fontSize: 14.5,
                        color: 'var(--ink)',
                        ...jp,
                      }}
                    >
                      <span style={{ fontFamily: 'var(--font-vi)', fontWeight: 700, fontSize: 12, width: 18, color: 'var(--text-secondary)' }}>{k + 1}</span>
                      <span style={{ flex: 1 }}>{o}</span>
                      {(isAnswer || isPicked) && (
                        <span style={{ fontFamily: 'var(--font-vi)', fontSize: 11.5, fontWeight: 700, color: tone, whiteSpace: 'nowrap' }}>{isAnswer ? 'Đáp án' : 'Bạn chọn'}</span>
                      )}
                    </div>
                  );
                })}
                {q.picked == null && <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>Bạn chưa trả lời câu này.</div>}
              </div>
              <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5, marginTop: 10 }}>{q.explanation}</div>
            </div>
          );
        })}
      </div>,
    );
  }

  /* ---------- trang chủ bài test ---------- */
  const items = history.data?.items ?? [];
  const rows = board.data ? [...board.data.rows, ...(board.data.me ? [board.data.me] : [])] : [];
  return shell(
    <Header title="Bài test" sub={`Unit ${data.fromBai}–${data.toBai} · Đề dạng JLPT N5`} onBack={onExit} />,
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--ink)' }}>{title}</div>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>{`Minna no Nihongo Bài ${data.fromBai}–${data.toBai} · ${total} câu · 3 phần × ${minutes} phút`}</div>
      </div>
      <Pill
        items={[
          ['do', 'Làm bài'],
          ['history', `Lịch sử${items.length ? ` (${items.length})` : ''}`],
          ['rank', 'Xếp hạng'],
        ]}
        value={tab}
        onChange={setTab}
      />
      {notice && <div style={{ fontSize: 13.5, color: 'var(--error)' }}>{notice}</div>}

      {tab === 'do' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {parts.map((list, i) => (
            <div key={i} style={{ ...card, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 14 }}>
              <span
                style={{ width: 44, height: 44, flexShrink: 0, borderRadius: '50%', background: 'var(--surface-tint)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: 'var(--cta-default)', ...jp }}
              >
                {PART_META[i].short}
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 15, fontWeight: 600, color: 'var(--ink)' }}>{`Phần ${i + 1} · ${PART_META[i].vi}`}</span>
                <span style={{ display: 'block', fontSize: 12.5, color: 'var(--text-secondary)', ...jp }}>{`${PART_META[i].jp} · ${minutes} phút`}</span>
              </span>
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{`${list.length} câu`}</span>
            </div>
          ))}
          <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5, margin: '2px 2px 0' }}>
            Phần nghe phát bằng giọng đọc tự động. Bạn tự chọn làm phần nào trước; điểm chỉ được tính khi bấm Nộp bài và nhập tên để lên bảng xếp hạng. Làm lại bao nhiêu lần cũng được.
          </div>
        </div>
      )}

      {tab === 'history' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {history.loading && <LoadingState />}
          {!history.loading && items.length === 0 && <div style={{ fontSize: 14, color: 'var(--text-secondary)', textAlign: 'center', marginTop: 12 }}>Chưa có lượt nộp bài nào.</div>}
          {items.map((a, i) => (
            <button
              key={a.id}
              onClick={() => openAttempt(a.id)}
              style={{ ...card, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left', width: '100%' }}
            >
              <span style={{ width: 56, flexShrink: 0, textAlign: 'center' }}>
                <span style={{ display: 'block', fontSize: 22, fontWeight: 700, color: a.passed ? 'var(--success)' : 'var(--ink)', lineHeight: 1 }}>{a.totalScore}</span>
                <span style={{ display: 'block', fontSize: 11, color: 'var(--text-secondary)' }}>/180</span>
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>{`Lần ${items.length - i} · ${formatAttemptMeta(a)}`}</span>
                <span style={{ display: 'block', fontSize: 12.5, color: 'var(--text-secondary)' }}>
                  {`Đúng ${a.correct}/${a.total} · ${formatClock(a.durationSeconds)} · ${a.passed ? 'Đạt' : 'Chưa đạt'}`}
                </span>
              </span>
              {WEB_ICO.chev('var(--cta-default)', 18)}
            </button>
          ))}
        </div>
      )}

      {tab === 'rank' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Pill
            items={[
              ['score', 'Theo điểm'],
              ['time', 'Theo thời gian'],
            ]}
            value={rankSort}
            onChange={setRankSort}
          />
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
            {rankSort === 'score'
              ? 'Lấy lượt tốt nhất của mỗi người. Điểm cao xếp trước; bằng điểm thì ai nhanh hơn xếp trước.'
              : 'Chỉ tính lượt đạt mức đỗ; thời gian làm ngắn nhất xếp trước.'}
          </div>
          {board.loading && <LoadingState />}
          {board.data && rows.length === 0 && (
            <div style={{ fontSize: 14, color: 'var(--text-secondary)', textAlign: 'center' }}>
              {rankSort === 'time' ? 'Chưa có lượt đạt mức đỗ nào.' : 'Chưa ai nộp bài — hãy là người đầu tiên!'}
            </div>
          )}
          {rows.length > 0 && (
            <div style={{ ...card, overflow: 'hidden' }}>
              {rows.map((r, i) => (
                <div
                  key={`${r.rank}-${i}`}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px', borderTop: i ? '1px solid var(--border-default)' : 'none', background: r.isMe ? 'var(--surface-tint)' : 'transparent' }}
                >
                  <span style={{ width: 26, fontSize: 14, fontWeight: 700, color: r.rank <= 3 ? 'var(--ink)' : 'var(--text-secondary)', textAlign: 'center' }}>{r.rank}</span>
                  <span
                    style={{
                      width: 32,
                      height: 32,
                      flexShrink: 0,
                      borderRadius: '50%',
                      background: r.isMe ? 'var(--cta-default)' : 'var(--surface-tint)',
                      color: r.isMe ? '#fff' : 'var(--navy-300)',
                      fontSize: 13,
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: r.isMe ? 'none' : '1px solid var(--border-default)',
                    }}
                  >
                    {(r.displayName.trim().split(/\s+/).pop() ?? '?').charAt(0).toUpperCase()}
                  </span>
                  <span style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: r.isMe ? 700 : 600, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {`${r.displayName}${r.isMe ? ' (Bạn)' : ''}`}
                  </span>
                  <span style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    <span style={{ display: 'block', fontSize: 14, fontWeight: 700, color: 'var(--ink)' }}>{rankSort === 'score' ? `${r.totalScore}/180` : formatClock(r.durationSeconds)}</span>
                    <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-secondary)' }}>{rankSort === 'score' ? formatClock(r.durationSeconds) : `${r.totalScore}/180`}</span>
                  </span>
                </div>
              ))}
            </div>
          )}
          {board.data && rows.length > 0 && !rows.some((r) => r.isMe) && (
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', textAlign: 'center' }}>
              {rankSort === 'time' ? 'Bạn chưa có lượt đạt mức đỗ để vào bảng này.' : 'Nộp bài để có tên trên bảng xếp hạng.'}
            </div>
          )}
        </div>
      )}
    </div>,
    tab === 'history' ? undefined : session ? (
      confirm === 'restart' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ fontSize: 13.5, color: 'var(--ink)', background: 'var(--surface-tint)', borderRadius: 10, padding: '10px 12px' }}>Bài đang làm sẽ bị xoá. Làm lại từ đầu?</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="secondary" size="lg" style={{ ...fullBtn, flex: 1 }} onClick={() => setConfirm(null)}>
              Huỷ
            </Button>
            <Button size="lg" style={{ ...fullBtn, flex: 1 }} onClick={startNew}>
              Làm lại
            </Button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <Button size="lg" style={fullBtn} onClick={() => setScreen('exam')}>
            {`Tiếp tục bài đang làm (${answeredIn(session, questions)}/${total})`}
          </Button>
          <Button variant="ghost" style={fullBtn} onClick={() => setConfirm('restart')}>
            Làm lại từ đầu
          </Button>
        </div>
      )
    ) : (
      <Button size="lg" style={fullBtn} onClick={startNew}>
        Bắt đầu làm bài
      </Button>
    ),
  );
}
